// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Trash2, Edit, Plus, Upload, Calendar, Rss, Image, Save, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Sport {
  id: string;
  name: string;
  gender: string | null;
  active: boolean;
}

interface SportsHubContent {
  id: string;
  sport_id: string;
  thumbnail_image_url: string | null;
  hero_image_url: string | null;
  description: string | null;
  calendar_feeds: CalendarFeed[];
  rss_feeds: RSSFeed[];
  featured_content: any;
  is_active: boolean;
  sort_order: number;
  meta_title: string | null;
  meta_description: string | null;
  sport?: Sport;
}

interface CalendarFeed {
  name: string;
  url: string;
  type: 'ical' | 'google' | 'outlook';
  enabled: boolean;
}

interface RSSFeed {
  name: string;
  url: string;
  category: string;
  enabled: boolean;
}

const SportsHubsManagement: React.FC = () => {
  const [sports, setSports] = useState<Sport[]>([]);
  const [sportsHubContent, setSportsHubContent] = useState<SportsHubContent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedHub, setSelectedHub] = useState<SportsHubContent | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    sport_id: '',
    description: '',
    is_active: true,
    sort_order: 0,
    meta_title: '',
    meta_description: '',
  });
  const [calendarFeeds, setCalendarFeeds] = useState<CalendarFeed[]>([]);
  const [rssFeeds, setRssFeeds] = useState<RSSFeed[]>([]);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [heroImageFile, setHeroImageFile] = useState<File | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);

      // Fetch sports
      const { data: sportsData, error: sportsError } = await supabase
        .from('sports')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (sportsError) throw sportsError;

      // Fetch sports hub content with sports data
      const { data: hubData, error: hubError } = await supabase
        .from('sports_hub_content')
        .select(`
          *,
          sport:sports(*)
        `)
        .order('sort_order');

      if (hubError) throw hubError;

      setSports(sportsData || []);
      setSportsHubContent(hubData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load sports hubs data');
    } finally {
      setIsLoading(false);
    }
  };

  const uploadFile = async (file: File, folder: string): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${folder}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('sports-hub-media')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('sports-hub-media')
        .getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
      return null;
    }
  };

  const handleSave = async () => {
    try {
      let thumbnailUrl = selectedHub?.thumbnail_image_url || null;
      let heroImageUrl = selectedHub?.hero_image_url || null;

      // Upload new thumbnail if provided
      if (thumbnailFile) {
        thumbnailUrl = await uploadFile(thumbnailFile, 'thumbnails');
      }

      // Upload new hero image if provided
      if (heroImageFile) {
        heroImageUrl = await uploadFile(heroImageFile, 'hero-images');
      }

      const hubData = {
        sport_id: formData.sport_id,
        thumbnail_image_url: thumbnailUrl,
        hero_image_url: heroImageUrl,
        description: formData.description,
        calendar_feeds: calendarFeeds,
        rss_feeds: rssFeeds,
        is_active: formData.is_active,
        sort_order: formData.sort_order,
        meta_title: formData.meta_title,
        meta_description: formData.meta_description,
      };

      if (isEditing && selectedHub) {
        // Update existing hub
        const { error } = await supabase
          .from('sports_hub_content')
          .update(hubData)
          .eq('id', selectedHub.id);

        if (error) throw error;
        toast.success('Sports hub updated successfully');
      } else {
        // Create new hub
        const { error } = await supabase
          .from('sports_hub_content')
          .insert(hubData);

        if (error) throw error;
        toast.success('Sports hub created successfully');
      }

      // Reset form and close dialog
      resetForm();
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving hub:', error);
      toast.error('Failed to save sports hub');
    }
  };

  const handleEdit = (hub: SportsHubContent) => {
    setSelectedHub(hub);
    setIsEditing(true);
    setFormData({
      sport_id: hub.sport_id,
      description: hub.description || '',
      is_active: hub.is_active,
      sort_order: hub.sort_order,
      meta_title: hub.meta_title || '',
      meta_description: hub.meta_description || '',
    });
    setCalendarFeeds(hub.calendar_feeds || []);
    setRssFeeds(hub.rss_feeds || []);
    setIsDialogOpen(true);
  };

  const handleDelete = async (hubId: string) => {
    if (!confirm('Are you sure you want to delete this sports hub?')) return;

    try {
      const { error } = await supabase
        .from('sports_hub_content')
        .delete()
        .eq('id', hubId);

      if (error) throw error;
      toast.success('Sports hub deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting hub:', error);
      toast.error('Failed to delete sports hub');
    }
  };

  const resetForm = () => {
    setSelectedHub(null);
    setIsEditing(false);
    setFormData({
      sport_id: '',
      description: '',
      is_active: true,
      sort_order: 0,
      meta_title: '',
      meta_description: '',
    });
    setCalendarFeeds([]);
    setRssFeeds([]);
    setThumbnailFile(null);
    setHeroImageFile(null);
  };

  const addCalendarFeed = () => {
    setCalendarFeeds([...calendarFeeds, { name: '', url: '', type: 'ical', enabled: true }]);
  };

  const addRSSFeed = () => {
    setRssFeeds([...rssFeeds, { name: '', url: '', category: '', enabled: true }]);
  };

  const removeCalendarFeed = (index: number) => {
    setCalendarFeeds(calendarFeeds.filter((_, i) => i !== index));
  };

  const removeRSSFeed = (index: number) => {
    setRssFeeds(rssFeeds.filter((_, i) => i !== index));
  };

  const updateCalendarFeed = (index: number, field: keyof CalendarFeed, value: any) => {
    const updated = [...calendarFeeds];
    updated[index] = { ...updated[index], [field]: value };
    setCalendarFeeds(updated);
  };

  const updateRSSFeed = (index: number, field: keyof RSSFeed, value: any) => {
    const updated = [...rssFeeds];
    updated[index] = { ...updated[index], [field]: value };
    setRssFeeds(updated);
  };

  const availableSports = sports.filter(sport => 
    !sportsHubContent.some(hub => hub.sport_id === sport.id) || 
    (isEditing && selectedHub?.sport_id === sport.id)
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-lg">Loading sports hubs...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Sports Hubs Management</h2>
          <p className="text-muted-foreground">
            Manage thumbnails, calendars, and RSS feeds for each sport hub
          </p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="w-4 h-4 mr-2" />
              Add Sports Hub
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {isEditing ? 'Edit Sports Hub' : 'Create Sports Hub'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sport">Sport</Label>
                  <Select 
                    value={formData.sport_id} 
                    onValueChange={(value) => setFormData({...formData, sport_id: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a sport" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableSports.map((sport) => (
                        <SelectItem key={sport.id} value={sport.id}>
                          {sport.name} {sport.gender && `(${sport.gender})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                  />
                  <Label>Active</Label>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Brief description of the sports hub"
                  rows={3}
                />
              </div>

              {/* Images */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Thumbnail Image</Label>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setThumbnailFile(e.target.files?.[0] || null)}
                  />
                  {selectedHub?.thumbnail_image_url && (
                    <img 
                      src={selectedHub.thumbnail_image_url} 
                      alt="Current thumbnail" 
                      className="w-20 h-20 object-cover rounded"
                    />
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label>Hero Image</Label>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setHeroImageFile(e.target.files?.[0] || null)}
                  />
                  {selectedHub?.hero_image_url && (
                    <img 
                      src={selectedHub.hero_image_url} 
                      alt="Current hero" 
                      className="w-20 h-20 object-cover rounded"
                    />
                  )}
                </div>
              </div>

              {/* Calendar Feeds */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Calendar Feeds</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addCalendarFeed}>
                    <Calendar className="w-4 h-4 mr-2" />
                    Add Calendar
                  </Button>
                </div>
                
                {calendarFeeds.map((feed, index) => (
                  <Card key={index}>
                    <CardContent className="p-4">
                      <div className="grid grid-cols-12 gap-4 items-center">
                        <div className="col-span-3">
                          <Input
                            placeholder="Feed name"
                            value={feed.name}
                            onChange={(e) => updateCalendarFeed(index, 'name', e.target.value)}
                          />
                        </div>
                        <div className="col-span-4">
                          <Input
                            placeholder="Calendar URL"
                            value={feed.url}
                            onChange={(e) => updateCalendarFeed(index, 'url', e.target.value)}
                          />
                        </div>
                        <div className="col-span-2">
                          <Select 
                            value={feed.type} 
                            onValueChange={(value) => updateCalendarFeed(index, 'type', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="ical">iCal</SelectItem>
                              <SelectItem value="google">Google</SelectItem>
                              <SelectItem value="outlook">Outlook</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2 flex items-center space-x-2">
                          <Switch
                            checked={feed.enabled}
                            onCheckedChange={(checked) => updateCalendarFeed(index, 'enabled', checked)}
                          />
                          <Label>Enabled</Label>
                        </div>
                        <div className="col-span-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeCalendarFeed(index)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* RSS Feeds */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">RSS Feeds</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addRSSFeed}>
                    <Rss className="w-4 h-4 mr-2" />
                    Add RSS Feed
                  </Button>
                </div>
                
                {rssFeeds.map((feed, index) => (
                  <Card key={index}>
                    <CardContent className="p-4">
                      <div className="grid grid-cols-12 gap-4 items-center">
                        <div className="col-span-3">
                          <Input
                            placeholder="Feed name"
                            value={feed.name}
                            onChange={(e) => updateRSSFeed(index, 'name', e.target.value)}
                          />
                        </div>
                        <div className="col-span-4">
                          <Input
                            placeholder="RSS URL"
                            value={feed.url}
                            onChange={(e) => updateRSSFeed(index, 'url', e.target.value)}
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            placeholder="Category"
                            value={feed.category}
                            onChange={(e) => updateRSSFeed(index, 'category', e.target.value)}
                          />
                        </div>
                        <div className="col-span-2 flex items-center space-x-2">
                          <Switch
                            checked={feed.enabled}
                            onCheckedChange={(checked) => updateRSSFeed(index, 'enabled', checked)}
                          />
                          <Label>Enabled</Label>
                        </div>
                        <div className="col-span-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeRSSFeed(index)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* SEO */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="meta_title">Meta Title</Label>
                  <Input
                    id="meta_title"
                    value={formData.meta_title}
                    onChange={(e) => setFormData({...formData, meta_title: e.target.value})}
                    placeholder="SEO title"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="sort_order">Sort Order</Label>
                  <Input
                    id="sort_order"
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({...formData, sort_order: parseInt(e.target.value) || 0})}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="meta_description">Meta Description</Label>
                <Textarea
                  id="meta_description"
                  value={formData.meta_description}
                  onChange={(e) => setFormData({...formData, meta_description: e.target.value})}
                  placeholder="SEO description"
                  rows={2}
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSave}>
                  <Save className="w-4 h-4 mr-2" />
                  {isEditing ? 'Update' : 'Create'} Sports Hub
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Sports Hubs List */}
      <div className="grid gap-6">
        {sportsHubContent.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <h3 className="text-lg font-semibold mb-2">No Sports Hubs Created</h3>
              <p className="text-muted-foreground mb-4">
                Create your first sports hub to get started with content management.
              </p>
            </CardContent>
          </Card>
        ) : (
          sportsHubContent.map((hub) => (
            <Card key={hub.id} className="overflow-hidden">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    {hub.thumbnail_image_url ? (
                      <img
                        src={hub.thumbnail_image_url}
                        alt={`${hub.sport?.name} thumbnail`}
                        className="w-16 h-16 object-cover rounded-lg border-2 border-primary/20"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-muted rounded-lg flex items-center justify-center">
                        <Image className="w-6 h-6 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="text-xl font-semibold">
                          {hub.sport?.name}
                        </h3>
                        {hub.sport?.gender && (
                          <Badge variant="secondary">
                            {hub.sport.gender}
                          </Badge>
                        )}
                        <Badge variant={hub.is_active ? "default" : "destructive"}>
                          {hub.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                      
                      {hub.description && (
                        <p className="text-muted-foreground mb-3">
                          {hub.description}
                        </p>
                      )}
                      
                      <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                        <span className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {hub.calendar_feeds?.length || 0} Calendar Feeds
                        </span>
                        <span className="flex items-center">
                          <Rss className="w-4 h-4 mr-1" />
                          {hub.rss_feeds?.length || 0} RSS Feeds
                        </span>
                        {hub.hero_image_url && (
                          <span className="flex items-center">
                            <Image className="w-4 h-4 mr-1" />
                            Hero Image
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(hub)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(hub.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default SportsHubsManagement;